
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  DollarSign,
  TrendingUp,
  Activity,
  Users,
  Truck,
  Heart,
  Sparkles,
  Brain,
  ArrowUpRight,
} from "lucide-react";

import { KpiCard } from '../components/KpiCard';
import { PageHeader } from '../components/PageHeader';
import { ChartCard } from '../components/ChartCard';
import { ValueDeliveredPanel } from '../components/ValueDeliveredPanel';
import { PinnedReportCard, ReportPickerModal } from '../components/DepartmentCockpit';
import { Button } from '../components/ui/button';

import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import { getUserAccountId, getUserRole } from '../lib/access';
import { SET_REPORT_DATA } from '../../store/actions';

import {
  kpiCards,
  segmentMix,
  recentReports,
} from '../lib/mock-data';


/* =========================================================
   ICON MAP
========================================================= */

const iconMap = {
  DollarSign,
  TrendingUp,
  Activity,
  Users,
  Truck,
  Heart,
};


/* =========================================================
   OVERVIEW KPI MAPPING
   Backend KPI code -> Overview card
========================================================= */

const normalizeDepartment = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');
const departmentKey = (value) => {
  const department = normalizeDepartment(value);
  if (department === 'human-capital') return 'hr';
  if (department === 'supply-chain') return 'supply';
  if (department === 'production') return 'operations';
  return department;
};


/* =========================================================
   REVENUE TREND
   Kept exactly as your existing Overview UI.
========================================================= */

const overviewRevenueTrend = [
  { month: "Jan", revenue: 132, forecast: 128 },
  { month: "Feb", revenue: 142, forecast: 138 },
  { month: "Mar", revenue: 155, forecast: 150 },
  { month: "Apr", revenue: 150, forecast: 154 },
  { month: "May", revenue: 162, forecast: 160 },
  { month: "Jun", revenue: 171, forecast: 165 },
  { month: "Jul", revenue: 158, forecast: 168 },
  { month: "Aug", revenue: 164, forecast: 170 },
  { month: "Sep", revenue: 178, forecast: 174 },
  { month: "Oct", revenue: 183, forecast: 180 },
  { month: "Nov", revenue: 190, forecast: 186 },
  { month: "Dec", revenue: 208, forecast: 198 },
];


const GREEN_CHART = {
  revenue: "#00843d",
  forecast: "#009b63",
  fill: "#8fd6b0",
  divider: "var(--color-card)",
};


const PIE_COLORS = [
  "#00843d",
  "#00a56f",
  "#79a342",
  "#938400",
];


/* =========================================================
   KPI VALUE FORMATTER
========================================================= */

const formatKpiValue = (value, cardId, kpiCode = '') => {
  if (value == null) return '-';

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  const metricHint = String(kpiCode || '').toLowerCase();

  if (/pct|percent|margin|rate|otif|retention|churn|utili[sz]ation/.test(metricHint)) {
    return `${numericValue.toFixed(1)}%`;
  }

  if (/qty|quantity|count|headcount|units|orders|tickets|finished_goods/.test(metricHint)) {
    return Math.round(numericValue).toLocaleString();
  }

  if (/\bdays?\b|time_to_hire|lead_time|cycle_time|turnaround|\btat\b/.test(metricHint)) {
    return `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 1 })} days`;
  }

  /*
   * Sales
   */
  if (cardId === 'sales') {
    return numericValue.toFixed(1);
  }

  /*
   * Customer
   */
  if (cardId === 'customer') {
    return `${Math.round(numericValue)} NPS`;
  }

  /*
   * HR / Operations
   */
  if (cardId === 'hr' || cardId === 'operations') {
    return Math.round(numericValue).toLocaleString();
  }

  /*
   * Finance / monetary values
   */
  if (Math.abs(numericValue) >= 10000000) {
    return `Rs.${(numericValue / 10000000).toFixed(1)}Cr`;
  }

  if (Math.abs(numericValue) >= 100000) {
    return `Rs.${(numericValue / 100000).toFixed(1)}L`;
  }

  if (Math.abs(numericValue) >= 1000) {
    return `Rs.${(numericValue / 1000).toFixed(1)}K`;
  }

  return numericValue.toLocaleString();
};


/* =========================================================
   DELTA
========================================================= */

const formatDelta = (value, previous) => {
  if (value == null || previous == null) {
    return '-';
  }

  const currentValue = Number(value);
  const previousValue = Number(previous);

  if (
    !Number.isFinite(currentValue) ||
    !Number.isFinite(previousValue) ||
    previousValue === 0
  ) {
    return '-';
  }

  const change =
    ((currentValue - previousValue) / previousValue) * 100;

  return `${Math.abs(change).toFixed(1)}% vs prev`;
};


/* =========================================================
   CHECK KPI
========================================================= */

const hasUsableKpiMetric = (metric) => {
  if (!metric || metric.value == null) {
    return false;
  }

  const currentValue = Number(metric.value);

  return Number.isFinite(currentValue);
};


/* =========================================================
   OVERVIEW COMPONENT
========================================================= */

export function Overview() {

  const account = useSelector((s) => s.account);
  const dispatch = useDispatch();
  const history = useHistory();

  const firstName =
    account?.user?.first_name ||
    account?.org_member?.first_name ||
    'there';


  /* =======================================================
     ORGANIZATION ID
  ======================================================= */

  const orgId =
    account?.org?.id ||
    account?.org?.data?.id ||
    account?.org_data?.id ||
    account?.org_data?.data?.id ||
    account?.org_member?.org_id ||
    account?.UserDetails?.org_id ||
    account?.user?.org_id;

  const userAccountId = getUserAccountId(account || {});
  const isOwner = getUserRole(account || {}) === 'Owner';
  const orgMemberId = account?.org_member?.id || account?.UserDetails?.id || account?.user?.org_member_id || null;
  const [reports, setReports] = useState([]);
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [overviewConfigLoaded, setOverviewConfigLoaded] = useState(false);
  const [myReport, setMyReport] = useState({ reportId: null, reportTitle: null, datasetId: null, groupId: null, pbReportId: null, isSecure: false, rlsRole: null, embedUrl: null, lastViewed: null });

  useEffect(() => {
    if (!orgMemberId) return;
    const apiBase = String(configData.API_SERVER || '').replace(/\/+$/, '');
    axiosInstance.get(`${apiBase}/user/allocated-reports`, { params: { org_member_id: orgMemberId } })
      .then(response => {
        const enrich = (items = []) => items.map(report => ({
          ...report,
          pbReportId: report.pbReportId || report.reportId || null,
          embedUrl: report.embedUrl || report.embedURL || null,
          children: enrich(report.children || []),
        }));
        setReports(enrich(response?.data || []));
      })
      .catch(error => console.error('Overview: failed to load allocated reports', error));
  }, [orgMemberId]);

  useEffect(() => {
    if (!orgId || !orgMemberId) return;
    setOverviewConfigLoaded(false);
    axiosInstance.get(`${configData.API_SERVER}user/cockpit-config/`, {
      params: { org_id: orgId, user_id: orgMemberId, section: 'overview' },
    }).then(response => {
      if (response?.data?.my_report) setMyReport(response.data.my_report);
    }).catch(() => {}).finally(() => setOverviewConfigLoaded(true));
  }, [orgId, orgMemberId]);

  useEffect(() => {
    if (!orgId || !orgMemberId || !overviewConfigLoaded) return;
    const timer = setTimeout(() => {
      axiosInstance.post(`${configData.API_SERVER}user/cockpit-config/`, {
        org_id: orgId,
        user_id: orgMemberId,
        section: 'overview',
        my_report: myReport,
        kpi_cards: [],
        period: 'QTD',
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [myReport, orgId, orgMemberId, overviewConfigLoaded]);

  const findReport = (items, nodeId) => {
    for (const report of (items || [])) {
      if (String(report.id || '') === String(nodeId || '')) return report;
      const child = findReport(report.children || [], nodeId);
      if (child) return child;
    }
    return null;
  };

  const openPinnedReport = async () => {
    const report = findReport(reports, myReport.reportId);
    if (!report) return;
    const embedUrl = report.embedURL || report.embedUrl || myReport.embedUrl || null;
    const powerBiReportId = report.reportId || myReport.pbReportId || null;
    const datasetId = report.datasetId || myReport.datasetId || null;
    const groupId = report.groupId || myReport.groupId || null;
    const rlsRole = report.rlsRole || myReport.rlsRole || null;
    const isSecure = report.isSecure === true || myReport.isSecure === true || String(embedUrl || '').includes('/reportEmbed');

    try {
      if (isSecure) {
        const tokenResponse = await axiosInstance.post(
          `${configData.API_SERVER}user/get-powerbi-embed-token`,
          { reportId: powerBiReportId, datasetId, orgId, rlsRole, groupId },
          { headers: { 'Content-Type': 'application/json' } },
        );
        const tokenData = tokenResponse?.data || {};
        const finalGroupId = tokenData.groupId || groupId;
        const finalEmbedUrl = tokenData.embedUrl || (powerBiReportId && finalGroupId
          ? `https://app.powerbi.com/reportEmbed?reportId=${powerBiReportId}&groupId=${finalGroupId}`
          : embedUrl);
        dispatch({ type: SET_REPORT_DATA, payload: {
          title: myReport.reportTitle || report.title,
          reportId: powerBiReportId,
          isSecure: true,
          embedUrl: finalEmbedUrl,
          reportData: finalEmbedUrl,
          accessToken: tokenData.embedToken,
          datasetId: tokenData.datasetId || datasetId,
          groupId: finalGroupId,
        }});
      } else {
        dispatch({ type: SET_REPORT_DATA, payload: {
          title: myReport.reportTitle || report.title,
          reportId: powerBiReportId,
          isSecure: false,
          embedUrl,
          reportData: embedUrl,
          datasetId,
          groupId,
        }});
      }
      setMyReport(current => ({ ...current, lastViewed: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }));
      history.push('/imagei/report');
    } catch (error) {
      console.error('Overview: failed to open pinned report', error);
    }
  };


  /* =======================================================
     KPI STATE
  ======================================================= */

  const [kpiData, setKpiData] = useState({});
  const [kpiMaster, setKpiMaster] = useState([]);


  /* =======================================================
     FETCH KPI DATA
  ======================================================= */

  useEffect(() => {

    if (!orgId) {
      setKpiData({});
      return;
    }

    const controller = new AbortController();


    const loadOverviewKpis = async () => {

      try {

        const apiUrl =
          `${String(configData.API_SERVER || '').replace(/\/+$/, '')}` +
          `/activity/dashboard_metrics/`;


        const response = await axiosInstance.get(
          apiUrl,
          {
            params: {
              org_id: orgId,
              // Overview needs one important KPI from every department in the
              // organization's KPI Master. Sending user_id together with
              // org_id applies the legacy allocation filter and drops entire
              // departments (notably Human Capital and Customer).
              ...(!orgId && !isOwner && userAccountId ? { user_id: userAccountId } : {}),
              period: 'QTD',

              /*
               * IMPORTANT:
               *
               * Overview contains KPIs from multiple departments.
               *
               * Therefore section must be "all".
               */
              section: 'all',
            },

            signal: controller.signal,
          }
        );


        if (!controller.signal.aborted) {

          setKpiData(
            response?.data?.metrics || {}
          );

          setKpiMaster(
            response?.data?.kpi_master || response?.data?.kpiMaster || []
          );

        }

      } catch (error) {

        if (
          error?.name !== 'CanceledError' &&
          error?.name !== 'AbortError'
        ) {

          console.error(
            'Overview: failed to load organization KPI data',
            error
          );

          setKpiData({});

        }

      }

    };


    loadOverviewKpis();


    return () => {
      controller.abort();
    };

  }, [orgId, userAccountId, isOwner]);

  /* =======================================================
     CREATE DYNAMIC KPI CARDS
     
     IMPORTANT:
     Existing kpiCards still controls:
       - title
       - icon
       - href
       - description
       - styling

     API controls:
       - metric
       - delta
       - trend
  ======================================================= */

  const masterByCode = new Map(
    kpiMaster.map((row) => [String(row.kpi_code || row.kpiCode || '').toUpperCase(), row])
  );
  const cardsToRender = kpiCards.map((overviewCard) => {
    const firstMasterForDepartment = [...kpiMaster]
      .filter(row => departmentKey(row.department) === overviewCard.id)
      .sort((a, b) => Number(a.sr_no || a.srNo || 999) - Number(b.sr_no || b.srNo || 999))[0];
    const kpiCode = firstMasterForDepartment?.kpi_code || firstMasterForDepartment?.kpiCode;
    if (!kpiCode) return null;
    return {
      kpiCode,
      overviewCard,
    };
  }).filter(Boolean);

  const dynamicKpiCards = cardsToRender.map((savedCard) => {
    const kpiCode = String(savedCard.kpiCode || '').toUpperCase();
    const masterRow = masterByCode.get(kpiCode);
    const frontendCard = savedCard.overviewCard;
    const department = departmentKey(masterRow?.department);
    const card = {
      ...frontendCard,
      id: frontendCard?.id || department,
      description: masterRow
        ? (masterRow.kpi_card_name || masterRow.kpiCardName || frontendCard?.description)
        : frontendCard?.description,
    };

    const metric =
      kpiData?.[kpiCode] || null;

    /*
     * If backend doesn't have this KPI yet,
     * keep the card but show "-".
     */
    if (!hasUsableKpiMetric(metric)) {

      return {
        ...card,

        metric: '-',

        delta: '-',

        trend: 'up',

        icon:
          typeof card.icon === 'string'
            ? iconMap[card.icon]
            : card.icon,
      };

    }


    const currentValue =
      Number(metric.value ?? 0);


    const previousValue =
      Number(metric.prev ?? 0);


    const change =
      previousValue
        ? ((currentValue - previousValue) / previousValue) * 100
        : 0;


    return {

      ...card,

      /*
       * Replace mock metric with DB/API value
       */
      metric: formatKpiValue(
        currentValue,
        frontendCard?.id || department,
        kpiCode
      ),


      /*
       * Replace mock delta with API calculated delta
       */
      delta: formatDelta(
        currentValue,
        previousValue
      ),


      /*
       * Calculate trend
       */
      trend:
        change >= 0
          ? 'up'
          : 'down',


      /*
       * Convert string icon name
       * into Lucide component
       */
      icon:
        typeof card.icon === 'string'
          ? iconMap[card.icon]
          : card.icon,
    };

  });


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="min-h-screen">

      {/* ===================================================
          HEADER
      =================================================== */}

      <PageHeader
        eyebrow="Executive Overview"
        title={`Welcome back, ${firstName}`}
        description="Cross-functional snapshot — click any card to preview the report, or use the link to open the full dashboard."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-2" onClick={() => history.push('/imagei/reconciliation')}>
              <Brain className="h-4 w-4" />
              Decision Intelligence
            </Button>
            <Button className="gap-2" onClick={() => history.push('/imagei/agents')}>
              <Sparkles className="h-4 w-4" />
              Ask AI Agent
            </Button>
          </div>

        }
      />


      {/* ===================================================
          KPI CARDS
      =================================================== */}

      <section className="px-6 lg:px-10 -mt-2">

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">

          {dynamicKpiCards.map((card) => (

            <KpiCard
              key={card.id}
              title={card.title}
              metric={card.metric}
              delta={card.delta}
              trend={card.trend}
              description={card.description}
              icon={card.icon}
              href={card.href}
              color={card.color}
              recon={card.recon}
            />

          ))}

        </div>

      </section>

      <ValueDeliveredPanel orgMember={account?.org_member} />

      <section className="px-6 lg:px-10 mt-8">
        <div className="flex items-end justify-between gap-4 mb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Executive reporting</p>
            <h2 className="font-display text-xl font-semibold mt-1">Pinned Power BI dashboard</h2>
            <p className="text-sm text-muted-foreground mt-1">Your single consolidated CEO/CFO dashboard, selected from reports allocated to you.</p>
          </div>
        </div>
        <div className="max-w-xl">
          <PinnedReportCard
            myReport={myReport}
            onOpen={openPinnedReport}
            onConfigure={() => setShowReportPicker(true)}
            editMode
          />
        </div>
      </section>


      {/* ===================================================
          REVENUE + SEGMENT
      =================================================== */}

      <section className="px-6 lg:px-10 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">


        {/* =================================================
            REVENUE CHART
        ================================================= */}

        <ChartCard
          title="Revenue vs Forecast"
          subtitle="Trailing 12 months — USD millions"
          className="lg:col-span-2 h-[340px]"
          actions={
            <span className="text-xs font-medium text-success">
              +14.6% YoY
            </span>
          }
        >

          <ResponsiveContainer
            width="100%"
            height="100%"
          >

            <AreaChart
              data={overviewRevenueTrend}
              margin={{
                top: 10,
                right: 10,
                left: -10,
                bottom: 0,
              }}
            >

              <defs>

                <linearGradient
                  id="overviewRevenueFill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >

                  <stop
                    offset="0%"
                    stopColor={GREEN_CHART.fill}
                    stopOpacity={0.45}
                  />

                  <stop
                    offset="100%"
                    stopColor={GREEN_CHART.fill}
                    stopOpacity={0.05}
                  />

                </linearGradient>

              </defs>


              <CartesianGrid
                stroke="var(--color-border)"
                strokeDasharray="3 3"
                vertical={false}
              />


              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />


              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                domain={[0, 220]}
                ticks={[0, 55, 110, 165, 220]}
              />


              <Tooltip
                contentStyle={{
                  background:
                    "var(--color-popover)",
                  border:
                    "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />


              <Area
                type="monotone"
                dataKey="revenue"
                stroke={GREEN_CHART.revenue}
                strokeWidth={2}
                fill="url(#overviewRevenueFill)"
              />


              <Area
                type="monotone"
                dataKey="forecast"
                stroke={GREEN_CHART.forecast}
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="none"
              />

            </AreaChart>

          </ResponsiveContainer>

        </ChartCard>


        {/* =================================================
            SEGMENT MIX
        ================================================= */}

        <ChartCard
          title="Segment Mix"
          subtitle="Revenue contribution"
          className="h-[340px]"
        >

          <ResponsiveContainer
            width="100%"
            height="100%"
          >

            <PieChart>

              <Pie
                data={segmentMix}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={3}
                stroke={GREEN_CHART.divider}
                strokeWidth={4}
              >

                {segmentMix.map((_, i) => (

                  <Cell
                    key={i}
                    fill={
                      PIE_COLORS[
                        i % PIE_COLORS.length
                      ]
                    }
                  />

                ))}

              </Pie>


              <Tooltip
                contentStyle={{
                  background:
                    "var(--color-popover)",
                  border:
                    "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />

            </PieChart>

          </ResponsiveContainer>


          <ul className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">

            {segmentMix.map((s) => (

              <li
                key={s.name}
                className="flex items-center gap-3 text-muted-foreground"
              >

                {s.name}

                <span className="text-foreground/80 ml-auto">
                  {s.value}%
                </span>

              </li>

            ))}

          </ul>

        </ChartCard>

      </section>

      {showReportPicker && (
        <ReportPickerModal
          reports={reports}
          current={myReport}
          onSelect={report => {
            setMyReport({
              reportId: report.id || report.reportId || null,
              reportTitle: report.title || report.name || null,
              datasetId: report.datasetId || null,
              groupId: report.groupId || null,
              pbReportId: report.pbReportId || report.reportId || null,
              isSecure: report.isSecure || false,
              rlsRole: report.rlsRole || null,
              embedUrl: report.embedURL || report.embedUrl || null,
              lastViewed: null,
            });
            setShowReportPicker(false);
          }}
          onClose={() => setShowReportPicker(false)}
        />
      )}


      {/* ===================================================
          RECENT REPORTS
      =================================================== */}

      <section className="px-6 lg:px-10 mt-8">

        <div className="surface-card overflow-hidden">

          <div className="flex items-center justify-between p-5 border-b border-border/60">

            <div>

              <h3 className="text-sm font-semibold">
                Recent Reports
              </h3>

              <p className="text-xs text-muted-foreground mt-0.5">
                Embedded Power BI artifacts most viewed this week
              </p>

            </div>


            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground"
              asChild
            >

              <Link to="/reports">

                View all

                <ArrowUpRight className="h-3.5 w-3.5 ml-1" />

              </Link>

            </Button>

          </div>


          <div className="divide-y divide-border/60">

            {recentReports.map((r) => (

              <div
                key={r.id}
                className="grid grid-cols-12 items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors"
              >

                <div className="col-span-6 md:col-span-5">

                  <p className="text-sm font-medium">
                    {r.title}
                  </p>

                </div>


                <div className="col-span-3 text-xs text-muted-foreground">
                  {r.owner}
                </div>


                <div className="hidden md:block md:col-span-2 text-xs text-muted-foreground">
                  {r.updated}
                </div>


                <div className="col-span-3 md:col-span-2 text-right">

                  <span
                    className={
                      r.status === "Live"
                        ? "text-[11px] rounded-full px-2 py-0.5 border border-success/30 bg-success/10 text-success"
                        : "text-[11px] rounded-full px-2 py-0.5 border border-warning/30 bg-warning/10 text-warning"
                    }
                  >
                    {r.status}
                  </span>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

    </div>

  );

}
